import { useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { tooltipStyle, COLORS } from '../../utils/colors';
import { useColorScheme } from '../../hooks/useColorScheme';

/**
 * Vertical grouped bar chart showing distribution of responses across buckets.
 * Matches the mockup: x-axis = buckets (0-10, 11-20, ..., 91-100),
 * y-axis = percentage, one series per option, percentage labels on top.
 *
 * Props:
 *   data — { "Option A": [{ bucket: "0-10", count: 42 }, ...], ... }
 *   questionType — 'allocation' | 'slider'
 */
export default function DistributionChart({ data, questionType }) {
  const { palette } = useColorScheme();
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data) return;

    if (chartRef.current) chartRef.current.destroy();

    const optionNames = Object.keys(data);
    if (optionNames.length === 0) return;

    // Get bucket labels from first option
    const bucketLabels = data[optionNames[0]]?.map(b => b.bucket) || [];

    // Build one dataset per option (each option is a colored series)
    const datasets = optionNames.map((opt, i) => {
      const buckets = data[opt];
      const total = buckets.reduce((s, b) => s + b.count, 0);

      return {
        label: opt,
        data: buckets.map(b => total > 0 ? (b.count / total * 100) : 0),
        backgroundColor: palette[i % palette.length],
        borderWidth: 0,
        borderRadius: 3,
        barPercentage: 0.85,
        categoryPercentage: 0.8
      };
    });

    chartRef.current = new Chart(canvasRef.current, {
      type: 'bar',
      data: { labels: bucketLabels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 24 } },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { size: 11 },
              color: COLORS.textMuted
            }
          },
          y: {
            beginAtZero: true,
            grid: { color: '#f0f1f3' },
            ticks: {
              font: { size: 11 },
              color: COLORS.textMuted,
              callback: (v) => v + '%'
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipStyle,
            callbacks: {
              label: (item) => ` ${item.dataset.label}: ${item.raw.toFixed(1)}%`
            }
          },
          datalabels: {
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] > 3,
            anchor: 'end',
            align: 'top',
            color: COLORS.textSecondary,
            font: { size: 10, weight: 600 },
            formatter: (v) => v.toFixed(1) + '%',
            offset: 0
          }
        }
      }
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [data, questionType, palette]);

  return <canvas ref={canvasRef} />;
}
